import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { ChevronRight, Check, X, AlertCircle, ShoppingCart, Package, Zap, Brain, Workflow, GraduationCap, Building, Lock } from 'lucide-react';
import Nav from '../components/nav';
import SEO from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import styles from '../styles/services.module.css';
import { IoMdCloseCircleOutline } from "react-icons/io";
import { FaCheck } from "react-icons/fa6";
import { FiPlus, FiMinus } from "react-icons/fi";
import { baseUrl } from '../config';
import EnterpriseSupport from '../components/EnterpriseSupport';
import AutoLocationDetection from '../components/AutoLocationDetection';
import LocationPrompt, { LocationIndicator, PermissionDeniedMessage } from '../components/LocationPrompt';
import studentProgramBanner3 from '../assets/img/student-program3.jpg';

// ==================== CURRENCY CONFIGURATION ====================
const CURRENCY_CONFIG = {
    USD: { symbol: '$', name: 'US Dollar', rate: 1 },
    INR: { symbol: '₹', name: 'Indian Rupee', rate: 83.12 },
    EUR: { symbol: '€', name: 'Euro', rate: 0.92 },
    GBP: { symbol: '£', name: 'British Pound', rate: 0.79 },
    AUD: { symbol: 'A$', name: 'Australian Dollar', rate: 1.52 },
    CAD: { symbol: 'C$', name: 'Canadian Dollar', rate: 1.35 },
    SGD: { symbol: 'S$', name: 'Singapore Dollar', rate: 1.34 },
    AED: { symbol: 'د.إ', name: 'UAE Dirham', rate: 3.67 },
};

const COUNTRY_TO_CURRENCY = {
    US: 'USD', IN: 'INR', GB: 'GBP', AU: 'AUD', CA: 'CAD',
    SG: 'SGD', AE: 'AED', FR: 'EUR', DE: 'EUR', IT: 'EUR',
    ES: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR', IE: 'EUR',
    PT: 'EUR', GR: 'EUR', FI: 'EUR', LU: 'EUR',
};

// ==================== UI COMPONENTS ====================
const Alert = ({ children, className = '' }) => (
    <div className={`rounded-lg border p-4 ${className}`}>{children}</div>
);

const AlertDescription = ({ children }) => (
    <div className="text-sm text-gray-700">{children}</div>
);

const Card = ({ children, className = '' }) => (
    <div className={`rounded-xl border bg-white shadow ${className} relative`}>{children}</div>
);

const CardHeader = ({ children }) => (
    <div className="flex flex-col space-y-1.5 p-6">{children}</div>
);

const CardTitle = ({ children }) => (
    <h3 className="text-2xl font-semibold leading-none tracking-tight">{children}</h3>
);

const CardContent = ({ children }) => (
    <div className="p-6 pt-0">{children}</div>
);

const CardFooter = ({ children }) => (
    <div className="flex items-center p-6 pt-0">{children}</div>
);

const Badge = ({ children, className = '' }) => (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${className}`}>
        {children}
    </span>
);

// ==================== TABS IMPLEMENTATION ====================
const TabContext = React.createContext();

const Tabs = ({ children, defaultValue, className = '' }) => {
    const [activeTab, setActiveTab] = useState(defaultValue);
    return (
        <TabContext.Provider value={{ activeTab, setActiveTab }}>
            <div className={className}>{children}</div>
        </TabContext.Provider>
    );
};

const TabsList = ({ children, className = '' }) => (
    <div className={`inline-flex items-center justify-center rounded-lg bg-gray-100 p-1 ${className}`}>
        {children}
    </div>
);

const TabsTrigger = ({ children, value }) => {
    const { activeTab, setActiveTab } = React.useContext(TabContext);
    return (
        <button
            className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-base font-medium transition-all
                ${activeTab === value ? 'bg-white shadow text-black' : 'text-gray-600 hover:text-black'}`}
            onClick={() => setActiveTab(value)}
        >
            {children}
        </button>
    );
};

const TabsContent = ({ children, value }) => {
    const { activeTab } = React.useContext(TabContext);
    return <div className={activeTab === value ? 'block' : 'hidden'}>{children}</div>;
};

// ==================== TIER SELECTOR MODAL ====================
const TierSelectorModal = ({ isVisible, cart, formatPrice, onSelectTier, onClose, currency }) => {
    const [selectedTier, setSelectedTier] = useState('standard');

    if (!isVisible) return null;

    const toolsSubtotal = cart.reduce((total, item) => total + item.price, 0);

    const tiers = {
        basic: {
            name: 'Starter',
            price: 0.01,
            callsPerDay: 10,
            storage: '5 GB',
            support: 'Community Support',
            features: ['1 AI tool slot', '10 AI calls/day per tool', '5 GB Storage', 'Community Support']
        },
        standard: {
            name: 'Creator',
            price: 42,
            callsPerDay: 25,
            storage: '25 GB',
            support: 'Email Support 24hr',
            features: ['3 AI tool slots', '25 AI calls/day per tool', '25 GB Storage', 'Email Support (24hr)']
        },
        premium: {
            name: 'Professional',
            price: 120,
            callsPerDay: 37,
            storage: '100 GB',
            support: 'Priority Support (2hr)',
            features: ['All pre-production tools', '37 AI calls/day per tool', '100 GB Storage', 'Priority Support (2hr)', 'Advanced Analytics']
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center z-10">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Select Usage Tier for Your Tools</h2>
                        <p className="text-sm text-gray-600 mt-1">Choose the tier that fits your needs</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6">
                    <div className="bg-blue-50 rounded-lg p-4 mb-6">
                        <h3 className="font-semibold text-gray-900 mb-2">You selected {cart.length} tool{cart.length !== 1 ? 's' : ''}:</h3>
                        <ul className="space-y-1">
                            {cart.map((tool) => (
                                <li key={tool.id} className="text-sm text-gray-700 flex justify-between">
                                    <span>• {tool.name}</span>
                                    <span className="font-medium">{formatPrice(tool.price)}/mo</span>
                                </li>
                            ))}
                        </ul>
                        <div className="border-t border-blue-200 mt-3 pt-3">
                            <div className="flex justify-between font-semibold text-gray-900">
                                <span>Tools Subtotal:</span>
                                <span>{formatPrice(toolsSubtotal)}/mo</span>
                            </div>
                        </div>
                    </div>

                    <h3 className="font-bold text-lg text-gray-900 mb-4">Choose your usage tier:</h3>
                    <div className="grid grid-cols-3 gap-4">
                        {Object.entries(tiers).map(([key, tier]) => (
                            <div
                                key={key}
                                onClick={() => setSelectedTier(key)}
                                className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${selectedTier === key
                                    ? 'border-blue-500 bg-blue-50 shadow-lg scale-105'
                                    : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                                    }`}
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedTier === key ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                                                {selectedTier === key && <Check className="w-3 h-3 text-white" />}
                                            </div>
                                            <h4 className="font-bold text-lg text-gray-900">{tier.name}</h4>
                                        </div>
                                        {key === 'standard' && (
                                            <Badge className="bg-purple-100 text-purple-800 text-xs">Recommended</Badge>
                                        )}
                                    </div>
                                </div>

                                <div className="text-center mb-3">
                                    <div className="text-3xl font-bold text-gray-900">{formatPrice(tier.price)}</div>
                                    <div className="text-xs text-gray-500">per month</div>
                                </div>

                                <ul className="space-y-1.5 mb-3">
                                    {tier.features.map((feature, idx) => (
                                        <li key={idx} className="flex items-start gap-1.5 text-xs text-gray-700">
                                            <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="bg-gray-50 rounded-lg p-2 mt-3 text-xs">
                                    <div className="flex justify-between mb-1">
                                        <span className="text-gray-600">Tools:</span>
                                        <span className="font-medium">{formatPrice(toolsSubtotal)}</span>
                                    </div>
                                    <div className="flex justify-between mb-1">
                                        <span className="text-gray-600">Tier:</span>
                                        <span className="font-medium">{formatPrice(tier.price)}</span>
                                    </div>
                                    <div className="border-t border-gray-200 mt-1 pt-1 flex justify-between font-bold text-gray-900">
                                        <span>Total:</span>
                                        <span className="text-sm text-blue-600">{formatPrice(toolsSubtotal + tier.price)}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="sticky bottom-0 bg-gray-50 border-t p-6 flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onSelectTier(selectedTier)}
                        className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                    >
                        Continue to Checkout →
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==================== MAIN PRICING PAGE ====================
const PricingPage = () => {
    const [currency, setCurrency] = useState('USD');
    const [liveRate, setLiveRate] = useState(1); // live USD → local currency rate
    const [locationInfo, setLocationInfo] = useState(null);
    const [isLoadingLocation, setIsLoadingLocation] = useState(false);
    const [showLocationPrompt, setShowLocationPrompt] = useState(false);
    const [permissionStatus, setPermissionStatus] = useState('prompt');
    const [showPermissionDenied, setShowPermissionDenied] = useState(false);
    const [showEnterpriseChat, setShowEnterpriseChat] = useState(false);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [selectedPlan, setSelectedPlan] = useState('');
    const [cart, setCart] = useState([]);
    const [planCart, setPlanCart] = useState([]);
    const [showAlert, setShowAlert] = useState(true);
    const [activeUser, setActiveUser] = useState();
    const [devMode, setDevMode] = useState(false);
    const [limit, setLimit] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [quantity, setQuantity] = useState([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
    const [planQuantity, setPlanQuantity] = useState([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
    const [showTierSelector, setShowTierSelector] = useState(false);
    const [selectedTier, setSelectedTier] = useState('');
    const [purchaseMethod, setPurchaseMethod] = useState('');

    const navigate = useNavigate();

    // ✅ FIX: Auto-detect currency via IP directly — no GPS, no CSP issues, no callback closure
    useEffect(() => {
        isLoggedIn();

        const applyLocation = (countryCode, city, state, country) => {
            const detectedCurrency = COUNTRY_TO_CURRENCY[countryCode] || 'USD';
            setCurrency(detectedCurrency);
            setLocationInfo({ country, city, countryCode, currency: detectedCurrency });
            setIsLoadingLocation(false);

            // Fetch live rate from open.er-api.com (free, CORS-friendly)
            if (detectedCurrency === 'USD') {
                setLiveRate(1);
                return;
            }
            fetch('https://open.er-api.com/v6/latest/USD')
                .then(r => r.json())
                .then(data => {
                    const rate = data.rates?.[detectedCurrency];
                    if (rate) setLiveRate(rate);
                    else setLiveRate(CURRENCY_CONFIG[detectedCurrency]?.rate || 1);
                })
                .catch(() => {
                    setLiveRate(CURRENCY_CONFIG[detectedCurrency]?.rate || 1);
                });
        };

        // Try saved location first (instant, no API call)
        try {
            const saved = localStorage.getItem('userLocation');
            if (saved) {
                const loc = JSON.parse(saved);
                const daysDiff = (new Date() - new Date(loc.timestamp)) / (1000 * 60 * 60 * 24);
                if (daysDiff < 7 && loc.details) {
                    applyLocation(loc.details.countryCode, loc.details.city, loc.details.state, loc.details.country);
                    return;
                }
            }
        } catch (e) {}

        // Auto-detect by IP — instant, no browser permission dialog, already in CSP
        setIsLoadingLocation(true);
        fetch('https://ipapi.co/json/')
            .then(r => r.json())
            .then(data => {
                if (data.country_code) {
                    const locData = {
                        coordinates: [0, 0],
                        details: {
                            city: data.city || 'Unknown',
                            state: data.region || '',
                            country: data.country_name || 'Unknown',
                            countryCode: data.country_code || 'US',
                            formattedAddress: `${data.city}, ${data.region}, ${data.country_name}`
                        },
                        timestamp: new Date().toISOString()
                    };
                    localStorage.setItem('userLocation', JSON.stringify(locData));
                    applyLocation(data.country_code, data.city, data.region, data.country_name);
                } else {
                    setCurrency('USD');
                    setIsLoadingLocation(false);
                }
            })
            .catch(() => {
                setCurrency('USD');
                setIsLoadingLocation(false);
            });
    }, []);

    const handlePermissionStatusChange = useCallback((status) => {
        setPermissionStatus(status);
        if (status === 'denied') {
            setShowPermissionDenied(true);
            setShowLocationPrompt(false);
        }
    }, []);

    const handleLocationDetected = useCallback((locationData) => {
        setIsLoadingLocation(false);
        setShowLocationPrompt(false);
        setShowPermissionDenied(false);

        if (locationData && locationData.details) {
            const countryCode = locationData.details.countryCode;
            const detectedCurrency = COUNTRY_TO_CURRENCY[countryCode] || 'USD';
            setCurrency(detectedCurrency);
            setLocationInfo({
                country: locationData.details.country,
                city: locationData.details.city,
                countryCode: countryCode,
                currency: detectedCurrency
            });
        } else {
            setCurrency('USD');
        }
    }, []);

    const triggerLocationDetection = () => {
        setIsLoadingLocation(true);
        setShowLocationPrompt(false);

        fetch('https://ipapi.co/json/')
            .then(r => r.json())
            .then(data => {
                if (data.country_code) {
                    const locationData = {
                        coordinates: [0, 0],
                        details: {
                            city: data.city || 'Unknown',
                            state: data.region || '',
                            country: data.country_name || 'Unknown',
                            countryCode: data.country_code || 'US',
                            formattedAddress: `${data.city}, ${data.region}, ${data.country_name}`
                        },
                        timestamp: new Date().toISOString()
                    };
                    localStorage.setItem('userLocation', JSON.stringify(locationData));
                    handleLocationDetected(locationData);
                }
            })
            .catch(() => {
                setIsLoadingLocation(false);
                setCurrency('USD');
            });
    };

    const handleAllowLocation = () => {
        setIsLoadingLocation(true);
        setShowLocationPrompt(false);
        triggerLocationDetection();
    };

    const handleDeclineLocation = () => {
        setShowLocationPrompt(false);
        setCurrency('USD');
    };

    const handleChangeLocation = () => {
        setShowLocationPrompt(true);
    };

    const handleRetryLocation = () => {
        setShowPermissionDenied(false);
        setShowLocationPrompt(true);
    };

    // ===== CURRENCY CONVERSION =====
    const convertPrice = useCallback((usdPrice) => {
        const convertedPrice = usdPrice * liveRate;
        if (currency === 'INR') return Math.round(convertedPrice);
        return Math.round(convertedPrice * 100) / 100;
    }, [currency, liveRate]);

    const formatPrice = useCallback((usdPrice) => {
        const converted = convertPrice(usdPrice);
        const symbol = CURRENCY_CONFIG[currency]?.symbol || '$';
        if (currency === 'INR') return `${symbol}${converted.toLocaleString('en-IN')}`;
        return `${symbol}${converted.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }, [currency, convertPrice]);

    // ===== TOOLS CONFIGURATION =====
    const tools = useMemo(() => [
        {
            id: 1,
            name: 'Pre-Production',
            icon: <Brain className="w-6 h-6 text-purple-500" />,
            available: true,
            subTools: [
                { id: 1, name: 'Script Analysis', price: 19.99, description: 'AI-powered script breakdown and analysis', apiCalls: { standard: 10, premium: 37 } },
                { id: 2, name: 'Budgeting', price: 14.99, description: 'Intelligent budget forecasting and management', apiCalls: { standard: 8, premium: 30 } },
                { id: 3, name: 'Casting Assistant', price: 29.99, description: 'AI-driven talent matching and scheduling', apiCalls: { standard: 12, premium: 40 } },
                { id: 4, name: 'Location Scouting', price: 24.99, description: 'Virtual location scouting and management', apiCalls: { standard: 15, premium: 50 } },
            ],
        },
        {
            id: 2,
            name: 'Production',
            icon: <Workflow className="w-6 h-6 text-blue-500" />,
            available: false,
            comingSoon: true,
            subTools: [
                { id: 5, name: 'Progress Management', price: 29.99, description: 'Real-time production tracking and analytics' },
                { id: 6, name: 'Cast Management', price: 34.99, description: 'Comprehensive cast coordination and communication' },
                { id: 7, name: 'Scheduling Assistant', price: 39.99, description: 'AI-optimized production scheduling' },
                { id: 8, name: 'Documentation', price: 44.99, description: 'Automated documentation and version control' },
            ],
        },
        {
            id: 3,
            name: 'Post-Production',
            icon: <Package className="w-6 h-6 text-green-500" />,
            available: false,
            comingSoon: true,
            subTools: [
                { id: 9, name: 'Editing', price: 49.99, description: 'AI-assisted video editing and assembly' },
                { id: 10, name: 'Visual Effects', price: 54.99, description: 'Automated VFX suggestions and integration' },
                { id: 11, name: 'Marketing Analyst', price: 59.99, description: 'Data-driven marketing strategy optimization' },
                { id: 12, name: 'Advertisements', price: 64.99, description: 'AI-generated ad concepts and targeting' },
                { id: 13, name: 'Posters', price: 69.99, description: 'Automated poster design and A/B testing' },
                { id: 14, name: 'Trailers', price: 74.99, description: 'AI-assisted trailer editing and optimization' },
                { id: 15, name: 'Distribution', price: 79.99, description: 'Intelligent distribution strategy planning' },
            ],
        },
    ], []);

    // ===== PLANS CONFIGURATION =====
    const plans = useMemo(() => {
        const monthly = {
            basic: { price: 12, toolCredits: 1, callsPerDay: 10, features: ['1 AI Tool Slot', '10 AI Calls/Day per Tool', '5 GB Storage', 'Community Support', 'Monthly Updates'] },
            creator: { price: 42, toolCredits: 3, callsPerDay: 25, features: ['3 AI Tool Slots', '25 AI Calls/Day per Tool', '25 GB Storage', 'Email Support (24hr)', 'Standard Export Quality'] },
            professional: { price: 120, toolCredits: 'All', callsPerDay: 37, features: ['All Pre-Production Tools', '37 AI Calls/Day per Tool', '100 GB Storage', 'Priority Support (2hr)', 'Advanced Analytics', 'Team Collaboration', 'API Access'] },
            studio: { price: 361, toolCredits: 'Unlimited', callsPerDay: 'Unlimited', features: ['All Tools + Custom Dev', 'Unlimited AI Calls', '1 TB Storage', 'Dedicated Account Manager', 'White-Label Options', 'Advanced Security', 'News Channels / OTT Ready'] }
        };

        // Yearly is billed at a 20% discount (see calculateTotal() and the
        // breakdown UI, both of which already do monthlyPrice * 12 * 0.8).
        // The card always displays this next to a literal "/month" label,
        // even in yearly mode - so yearly.price must be the discounted
        // MONTHLY-EQUIVALENT rate (monthly.price * 0.8), not an annual
        // total. Derived here so it can't drift out of sync with monthly
        // again; only price changes per billing cycle, everything else
        // (toolCredits/callsPerDay/features) is identical.
        const yearly = Object.fromEntries(
            Object.entries(monthly).map(([key, plan]) => [
                key,
                { ...plan, price: Math.round(plan.price * 0.8 * 100) / 100 }
            ])
        );

        return { monthly, yearly };
    }, []);

    const fields = [
        { type: 'text', name: 'name', placeholder: 'Name', required: true },
        { type: 'email', name: 'email', placeholder: 'Email', required: true },
        { type: 'tel', name: 'phoneNumber', placeholder: 'Phone', required: false },
        { type: 'text', name: 'company', placeholder: 'Company', required: true },
        { type: 'textarea', name: 'message', placeholder: 'Message', required: true },
    ];

    async function isLoggedIn() {
        try {
            const userId = await fetch(`${baseUrl}/getUserIDByToken`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
            });
            if (userId.ok) {
                const userData = await userId.json();
                setActiveUser(userData.user_id);
            } else {
                toast.error('Please log in before using this page.', { icon: '🔒', duration: 3000 });
                navigate("/login");
            }
        } catch {
            toast.error('Please log in before using this page.', { icon: '🔒', duration: 3000 });
            navigate("/login");
        }
    }

    // ===== CART FUNCTIONS =====
    const addToCart = useCallback((tool) => {
        setCart(prev => prev.some(item => item.id === tool.id) ? prev : [...prev, tool]);
        let quan = [...quantity];
        quan[tool.id - 1] = 1;
        setQuantity(quan);
    }, [quantity]);

    const addToPlanCart = useCallback((tool) => {
        setPlanCart(prev => prev.some(item => item.id === tool.id) ? prev : [...prev, tool]);
        let quan = [...planQuantity];
        quan[tool.id - 1] = 1;
        setPlanQuantity(quan);
    }, [planQuantity]);

    const removeFromCart = useCallback((toolId) => {
        setCart(prev => prev.filter(item => item.id !== toolId));
        let quan = [...quantity];
        quan[toolId - 1] = 0;
        setQuantity(quan);
    }, [quantity]);

    const removeFromPlanCart = useCallback((toolId) => {
        setPlanCart(prev => prev.filter(item => item.id !== toolId));
        let quan = [...planQuantity];
        quan[toolId - 1] = 0;
        setPlanQuantity(quan);
    }, [planQuantity]);

    const calculateTotal = useCallback(() => {
        if (purchaseMethod === 'plan') {
            const toolsTotal = (sumArray(quantity) === 0 ? planCart : cart).reduce((total, item) => total + item.price, 0);
            const planPrice = selectedPlan && plans.monthly[selectedPlan] ? plans.monthly[selectedPlan].price : 0;
            const monthlyTotal = toolsTotal + planPrice;
            return billingCycle === 'yearly' ? monthlyTotal * 12 * 0.8 : monthlyTotal;
        } else {
            const toolsTotal = cart.reduce((total, item) => total + item.price, 0);
            const tierPrices = { basic: 12, standard: 42, premium: 120 };
            const tierPrice = tierPrices[selectedTier] || 0;
            const monthlyTotal = toolsTotal + tierPrice;
            return billingCycle === 'yearly' ? monthlyTotal * 12 * 0.8 : monthlyTotal;
        }
    }, [cart, planCart, selectedPlan, selectedTier, billingCycle, plans, quantity, purchaseMethod]);

    const handleTierSelection = (tier) => {
        setSelectedTier(tier);
        setShowTierSelector(false);
        setPurchaseMethod('tools');
        handleCheckout();
    };

    const handleCheckout = async () => {
        if (!selectedPlan && cart.length > 0 && !selectedTier) {
            setShowTierSelector(true);
            return;
        }

        try {
            setIsSubmitting(true);

            if (!activeUser) {
                alert('User session expired. Please log in again.');
                navigate('/login');
                return;
            }

            let monthly = billingCycle === "monthly";
            let requestBody = {
                user_id: activeUser,
                monthly: monthly,
                purchase_method: purchaseMethod || 'plan'
            };

            if (selectedPlan !== "") requestBody.plan = selectedPlan;
            else if (selectedTier !== "") requestBody.tier = selectedTier;

            const cartCreate = await fetch(`${baseUrl}/cart`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem("access_token")}` },
                body: JSON.stringify(requestBody)
            });

            if (!cartCreate.ok) {
                let errorMessage = 'Unknown error';
                try {
                    const errorData = await cartCreate.json();
                    if (errorData.detail) {
                        if (typeof errorData.detail === 'string') errorMessage = errorData.detail;
                        else if (Array.isArray(errorData.detail)) errorMessage = errorData.detail.map(err => `${err.loc.join('.')}: ${err.msg}`).join(', ');
                        else errorMessage = JSON.stringify(errorData.detail);
                    } else errorMessage = JSON.stringify(errorData);
                } catch { errorMessage = `HTTP ${cartCreate.status}: ${cartCreate.statusText}`; }
                alert(`❌ Failed to create cart:\n\n${errorMessage}\n\nStatus: ${cartCreate.status}`);
                setIsSubmitting(false);
                return;
            }

            const cartData = await cartCreate.json();
            const toolsToAdd = sumArray(quantity) === 0 ? planCart : cart;
            const quantityToUse = sumArray(quantity) === 0 ? planQuantity : quantity;

            if (sumArray(quantity) === 0 && sumArray(planQuantity) === 0) {
                setIsSubmitting(false);
                const el = document.getElementById("toolSelectContainer");
                if (el) el.style.display = "grid";
                return;
            }

            for (let i = 0; i < toolsToAdd.length; i++) {
                const element = toolsToAdd[i];
                const body = {
                    cart_id: cartData.cart_id,
                    tool_index: element.id,
                    tool_name: element.name,
                    tool_price: element.price,
                    quantity: quantityToUse[element.id - 1]
                };
                const cartTool = await fetch(`${baseUrl}/cartTool`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem("access_token")}` },
                    body: JSON.stringify(body)
                });

                if (!cartTool.ok) {
                    let errorMessage = 'Unknown error';
                    try {
                        const errorData = await cartTool.json();
                        errorMessage = errorData.detail || JSON.stringify(errorData);
                    } catch { errorMessage = `HTTP ${cartTool.status}`; }
                    alert(`❌ Failed to add ${element.name}:\n${errorMessage}`);
                    setIsSubmitting(false);
                    return;
                }
            }

            const totalAmountUSD = calculateTotal();
            const totalAmount = convertPrice(totalAmountUSD);
            const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

            if (isDevelopment && devMode) {
                const testCheckout = await fetch(`${baseUrl}/testCheckout`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem("access_token")}` },
                    body: JSON.stringify({ cart_id: cartData.cart_id, amount: totalAmount, billing_cycle: billingCycle })
                });
                if (!testCheckout.ok) {
                    let errorMessage = 'Unknown error';
                    try { const errorData = await testCheckout.json(); errorMessage = errorData.detail || JSON.stringify(errorData); }
                    catch { errorMessage = `HTTP ${testCheckout.status}`; }
                    alert(`❌ Dev checkout failed:\n${errorMessage}`);
                    setIsSubmitting(false);
                    return;
                }
                const checkoutData = await testCheckout.json();
                alert(`✅ DEV MODE: ${checkoutData.tools_activated?.length || 0} tools activated!`);
                navigate("/toolmanager");
                setIsSubmitting(false);
                return;
            }

            localStorage.setItem('payment_amount', totalAmount.toString());
            localStorage.setItem('billing_cycle', billingCycle);
            localStorage.setItem('user_id', activeUser.toString());
            localStorage.setItem('cart_id', cartData.cart_id);
            navigate("/payment/" + cartData.cart_id);
            setIsSubmitting(false);

        } catch (error) {
            alert(`💥 An error occurred during checkout:\n\n${error.message}\n\nPlease try again.`);
            setIsSubmitting(false);
        }
    };

    function closeToolSelect() {
        const el = document.getElementById("toolSelectContainer");
        if (el) el.style.display = "None";
        setPlanQuantity([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
        setPlanCart([]);
    }

    function lessQuantity(toolId) {
        if (quantity[toolId - 1] === 1) removeFromCart(toolId);
        else { let quan = [...quantity]; quan[toolId - 1] -= 1; setQuantity(quan); }
    }

    function addQuantity(toolId) {
        let quan = [...quantity]; quan[toolId - 1] += 1; setQuantity(quan);
    }

    function lessPlanQuantity(toolId) {
        if (planQuantity[toolId - 1] === 1) removeFromPlanCart(toolId);
        else { let quan = [...planQuantity]; quan[toolId - 1] -= 1; setPlanQuantity(quan); }
    }

    function addPlanQuantity(toolId) {
        let quan = [...planQuantity]; quan[toolId - 1] += 1; setPlanQuantity(quan);
    }

    function sumArray(arr) {
        return arr.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    }

    const FormField = ({ type, name, placeholder, value, onChange, required }) => {
        if (type === 'textarea') {
            return <textarea name={name} id={name} placeholder={placeholder} value={value} onChange={onChange} required={required} className="form-input" />;
        }
        return <input type={type} name={name} id={name} placeholder={placeholder} value={value} onChange={onChange} required={required} className="form-input" />;
    };

    const ContactForm = ({ onClose, title, fields }) => {
        const [formData, setFormData] = useState(fields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}));
        const [formStatus, setFormStatus] = useState('');
        const [success, setSuccess] = useState(false);
        const [isSubmitting, setIsSubmitting] = useState(false);

        const handleChange = (e) => {
            const { name, value } = e.target;
            setFormData({ ...formData, [name]: value });
        };

        const handleSubmit = async (e) => {
            setIsSubmitting(true);
            e.preventDefault();
            const dataToSend = { ...formData, category: "Contact-Sales", subject: "Contact-Sales", partner_type: "Null", integration: "Null", accountId: String(activeUser) };
            try {
                const response = await fetch(`${baseUrl}/contacts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dataToSend) });
                if (response.ok) { setIsSubmitting(false); setSuccess(true); setFormData(fields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {})); }
                else setFormStatus('An error occurred while submitting the form.');
            } catch { setFormStatus('An error occurred while submitting the form.'); }
        };

        return (
            <div className={`contact-form-overlay ${title ? 'active' : ''}`}>
                <div className="contact-form-container">
                    <button className="close-button" onClick={onClose}><X size={24} /></button>
                    <h2>{title}</h2>
                    <form onSubmit={handleSubmit} className="contact-form">
                        {fields.map((field) => (
                            <FormField key={field.name} type={field.type} name={field.name} placeholder={field.placeholder} value={formData[field.name]} onChange={handleChange} required={field.required} />
                        ))}
                        <button type="submit" disabled={isSubmitting} className="w-50 m-auto bg-gray-400 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-medium transition-colors duration-300 disabled:bg-gray-400">
                            {success ? "Message Sent!" : (isSubmitting ? (
                                <div className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Loading...
                                </div>
                            ) : 'Submit')}
                        </button>
                    </form>
                    {formStatus && <p className={`form-status ${formStatus.includes('successfully') ? 'success' : 'error'}`}>{formStatus}</p>}
                </div>
            </div>
        );
    };

    // ==================== RENDER ====================
    return (
        <>
            <SEO
              title="ProducerBuddy Services"
              description="Explore the full range of ProducerBuddy's film production services — script analysis, budgeting, casting, distribution, and AI tools for filmmakers worldwide."
              url="https://producerbuddy.pilomation.com/Services"
            />
            <AutoLocationDetection
                onLocationDetected={handleLocationDetected}
                onPermissionStatusChange={handlePermissionStatusChange}
                debug={false}
                autoTrigger={false}
            />

            <LocationPrompt
                isVisible={showLocationPrompt}
                currentCurrency={CURRENCY_CONFIG[currency]?.name || 'USD'}
                onAllow={handleAllowLocation}
                onDecline={handleDeclineLocation}
                onClose={handleDeclineLocation}
            />

            <TierSelectorModal
                isVisible={showTierSelector}
                cart={cart}
                formatPrice={formatPrice}
                onSelectTier={handleTierSelection}
                onClose={() => setShowTierSelector(false)}
                currency={currency}
            />

            <Nav />
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                    <div className="mb-4 flex justify-end">
                        <LocationIndicator
                            locationInfo={locationInfo}
                            currency={CURRENCY_CONFIG[currency]?.name}
                            onChangeLocation={handleChangeLocation}
                            isLoading={isLoadingLocation}
                        />
                    </div>

                    {showPermissionDenied && (
                        <PermissionDeniedMessage
                            onContinue={() => setShowPermissionDenied(false)}
                            onRetry={handleRetryLocation}
                        />
                    )}

                    {showAlert && (
                        <Alert className="mb-8 bg-blue-50 border-blue-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Zap className="w-5 h-5 text-blue-500" />
                                    <AlertDescription>🚀 Soft Launch: Pre-Production Tools Now Available!</AlertDescription>
                                </div>
                                <button onClick={() => setShowAlert(false)} className="text-gray-500 hover:text-gray-700">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </Alert>
                    )}

                    <div className="text-center mb-16">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">AI-Powered Production Tools</h1>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">Choose your plan and customize with AI tools for every stage of production</p>

                        <div className="mt-8 flex justify-center">
                            <div className="inline-flex rounded-md shadow-sm" role="group">
                                <button type="button" className={`px-4 py-2 text-sm font-medium ${billingCycle === 'monthly' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'} border border-gray-200 rounded-l-lg hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:bg-blue-700 focus:text-white`} onClick={() => setBillingCycle('monthly')}>
                                    Monthly billing
                                </button>
                                <button type="button" className={`px-4 py-2 text-sm font-medium ${billingCycle === 'yearly' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'} border border-l-0 border-gray-200 rounded-r-lg hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:bg-blue-700 focus:text-white`} onClick={() => setBillingCycle('yearly')}>
                                    Yearly billing
                                    <Badge className="ml-2 bg-green-200 text-green-800">Save 20%</Badge>
                                </button>
                            </div>
                        </div>
                    </div>

                    <Tabs defaultValue="plans" className="max-w-7xl mx-auto">
                        <TabsList className="grid w-full max-w-md grid-cols-2 mx-auto mb-8">
                            <TabsTrigger value="plans">
                                <div className="flex items-center gap-2"><Package className="w-4 h-4" />Subscription Plans</div>
                            </TabsTrigger>
                            <TabsTrigger value="tools">
                                <div className="flex items-center gap-2"><Brain className="w-4 h-4" />AI Tools</div>
                            </TabsTrigger>
                        </TabsList>

                        {/* PLANS TAB */}
                        <TabsContent value="plans">
                            <div className="grid grid-cols-4 gap-4" translate="no" suppressHydrationWarning={true}>
                                {Object.entries({
                                    basic: { title: "Starter", description: "Film enthusiasts, actors, crew exploring AI", color: "blue", badge: "Enthusiast", badgeColor: "bg-blue-100 text-blue-700" },
                                    creator: { title: "Creator", description: "Indie filmmakers, writers, small production teams", color: "indigo", badge: "Creator", badgeColor: "bg-indigo-100 text-indigo-700" },
                                    professional: { title: "Professional", description: "Medium production houses, OTT creators, directors", color: "purple", popular: true, badge: "Most Popular", badgeColor: "bg-purple-100 text-purple-700" },
                                    studio: { title: "Studio", description: "Large studios, cinema chains, news channels, OTT platforms", color: "gray", badge: "Enterprise", badgeColor: "bg-gray-100 text-gray-700" }
                                }).map(([key, { title, description, color, popular, badge, badgeColor }]) => (
                                    <div
                                        key={key}
                                        className={`relative flex flex-col bg-white rounded-2xl shadow-lg transition-all duration-300 w-full ${selectedPlan === key ? 'ring-4 ring-blue-500 scale-105 shadow-2xl' : 'hover:shadow-xl hover:scale-102'} cursor-pointer`}
                                        style={{ minHeight: '560px' }}
                                        onClick={() => {
                                            if (key !== 'studio') {
                                                if (!activeUser) return; // not logged in yet (or redirecting) - don't touch the DOM or schedule anything
                                                if (selectedPlan === key) {
                                                    const el = document.getElementById("toolSelectContainer");
                                                    if (el) el.style.display = "grid";
                                                } else {
                                                    setSelectedPlan(key);
                                                    setCart([]);
                                                    setPurchaseMethod('plan');
                                                    setQuantity([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
                                                    if (key === "professional") setLimit(4);
                                                    else if (key === "creator") setLimit(3);
                                                    else setLimit(1);
                                                    setTimeout(() => {
                                                        const el = document.getElementById("toolSelectContainer");
                                                        if (el) el.style.display = "grid";
                                                    }, 100);
                                                }
                                            }
                                        }}
                                    >
                                        {popular && (
                                            <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 z-10">
                                                <Badge className="bg-purple-600 text-white px-4 py-1.5 text-sm font-semibold shadow-lg">Most Popular</Badge>
                                            </div>
                                        )}
                                        {selectedPlan === key && (
                                            <div className="absolute top-6 right-6 bg-blue-500 rounded-full p-2 shadow-lg z-10">
                                                <Check className="w-5 h-5 text-white" />
                                            </div>
                                        )}
                                        <div className={`p-5 text-center text-white rounded-t-2xl ${key === 'basic' ? 'bg-blue-600' : key === 'creator' ? 'bg-indigo-600' : key === 'professional' ? 'bg-purple-600' : 'bg-gray-800'}`}>
                                            <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3 ${badgeColor}`}>{badge}</span>
                                            <h3 className="text-xl font-bold text-white mb-1">{title}</h3>
                                            <p className="text-white/70 text-xs mb-4">{description}</p>
                                            <div className="flex items-baseline justify-center mb-1">
                                                <span className="text-4xl font-extrabold text-white">{formatPrice(plans[billingCycle][key].price)}</span>
                                                <span className="text-white/70 ml-2 text-base">/month</span>
                                            </div>
                                            {billingCycle === 'yearly' && <p className="text-xs text-green-300 font-semibold">Billed annually • Save 20%</p>}
                                            {key !== 'studio' && <p className="text-xs text-white/60 mt-1">+ Individual tool subscriptions</p>}
                                        </div>
                                        <div className="p-5 flex-grow">
                                            <ul className="space-y-3">
                                                {plans[billingCycle][key].features.map((feature, idx) => (
                                                    <li key={idx} className="flex items-start gap-2">
                                                        <div className="flex-shrink-0 mt-0.5"><Check className="w-4 h-4 text-green-500" /></div>
                                                        <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="p-5 pt-0">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (key === 'studio') {
                                                        setShowEnterpriseChat(true);
                                                    } else if (!activeUser) {
                                                        return; // not logged in yet (or redirecting) - don't touch the DOM or schedule anything
                                                    } else if (selectedPlan === key) {
                                                        const el = document.getElementById("toolSelectContainer");
                                                        if (el) el.style.display = "grid";
                                                    } else {
                                                        setCart([]);
                                                        setSelectedPlan(key);
                                                        setPurchaseMethod('plan');
                                                        setQuantity([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
                                                        if (key === "professional") setLimit(4);
                                                        else if (key === "creator") setLimit(3);
                                                        else setLimit(1);
                                                        setTimeout(() => {
                                                            const el = document.getElementById("toolSelectContainer");
                                                            if (el) el.style.display = "grid";
                                                        }, 100);
                                                    }
                                                }}
                                                className={`w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 ${key === selectedPlan ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg' : key === 'studio' ? 'bg-gray-900 hover:bg-gray-800 text-white shadow-md' : 'bg-gray-50 hover:bg-gray-100 text-gray-900 border-2 border-gray-200'} transform hover:scale-105 hover:shadow-lg`}
                                            >
                                                {key === 'studio' && 'Contact Sales'}
                                                {key !== 'studio' && key !== selectedPlan && `Select ${plans[billingCycle][key].toolCredits} Tool${plans[billingCycle][key].toolCredits !== 1 ? 's' : ''} →`}
                                                {key === selectedPlan && '✓ Selected - Choose Tools'}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {billingCycle === 'yearly' && (
                                <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                                    <p className="text-green-800 text-sm font-medium">
                                        🎓 Film schools get 60% off Creator plan = {formatPrice(42 * 0.4)}/month per student seat
                                        <span className="text-green-600 ml-2">(institutional license negotiated separately)</span>
                                    </p>
                                </div>
                            )}
                        </TabsContent>

                        {/* TOOLS TAB */}
                        <TabsContent value="tools">
                            <div className="grid gap-4">
                                {tools.map((category) => (
                                    <Card key={category.id} className={!category.available ? 'opacity-60' : ''}>
                                        <CardHeader>
                                            <div className="flex items-center gap-3">
                                                {category.icon}
                                                <CardTitle>{category.name}</CardTitle>
                                                {category.comingSoon && (
                                                    <Badge className="bg-yellow-100 text-yellow-800 flex items-center gap-1">
                                                        <Lock className="w-3 h-3" />Coming Soon
                                                    </Badge>
                                                )}
                                                <ChevronRight className="w-4 h-4 text-gray-400" />
                                            </div>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="grid grid-cols-3 gap-4">
                                                {category.subTools.map((tool) => (
                                                    <div key={tool.id} className={`p-4 rounded-lg border border-gray-200 ${category.available ? 'hover:border-blue-500' : 'cursor-not-allowed'} transition-colors`}>
                                                        <div className="flex justify-between items-start mb-4">
                                                            <div>
                                                                <h4 className="font-medium text-gray-900">{tool.name}</h4>
                                                                <p className="text-sm text-gray-500 mt-1">{tool.description}</p>
                                                            </div>
                                                            <Badge className="bg-purple-100 text-purple-800">{formatPrice(tool.price)}/mo</Badge>
                                                        </div>
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-sm text-gray-600">
                                                                {category.available ? (cart.some(item => item.id === tool.id) ? 'Remove from cart' : 'Add to cart') : 'Coming Soon'}
                                                            </span>
                                                            <div className='flex items-center'>
                                                                {category.available && quantity[tool.id - 1] > 0 && <FiMinus className={styles.quantityButtonsMinus} onClick={() => lessQuantity(tool.id)} />}
                                                                <button
                                                                    onClick={() => {
                                                                        if (category.available) {
                                                                            cart.some(item => item.id === tool.id) ? removeFromCart(tool.id) : addToCart(tool);
                                                                            setSelectedPlan("");
                                                                            setPurchaseMethod('tools');
                                                                        }
                                                                    }}
                                                                    disabled={!category.available}
                                                                    className={`p-2 rounded-full transition-colors ${!category.available ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : (cart.some(item => item.id === tool.id) ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'bg-blue-100 text-blue-600 hover:bg-blue-200')}`}
                                                                >
                                                                    {category.available ? (cart.some(item => item.id === tool.id) ? <p className="w-5 text-sm">{quantity[tool.id - 1]}</p> : <ShoppingCart className="w-5 h-5" />) : <Lock className="w-5 h-5" />}
                                                                </button>
                                                                {category.available && quantity[tool.id - 1] > 0 && <FiPlus className={styles.quantityButtonsPlus} onClick={() => addQuantity(tool.id)} />}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </TabsContent>

                        {/* CART SUMMARY */}
                        {(selectedPlan || cart.length > 0) && (
                            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-20" translate="no" suppressHydrationWarning={true}>
                                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-8">
                                    <div className="flex items-center justify-between px-4">
                                        <div className="flex items-center gap-8">
                                            {selectedPlan && plans[billingCycle] && plans[billingCycle][selectedPlan] && (
                                                <div className="flex items-center gap-2">
                                                    <Package className="w-5 h-5 text-blue-500" />
                                                    <span className="font-medium">{selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} Plan ({formatPrice(plans[billingCycle][selectedPlan].price)})</span>
                                                </div>
                                            )}
                                            {(cart.length > 0 || planCart.length > 0) && (
                                                <div className="flex items-center gap-2">
                                                    <ShoppingCart className="w-5 h-5 text-purple-500" />
                                                    <span className="font-medium">
                                                        {purchaseMethod === 'plan'
                                                            ? `${planCart.length} Tool${planCart.length !== 1 ? 's' : ''} (${formatPrice(planCart.reduce((total, item) => total + item.price, 0))})`
                                                            : `${cart.length} Tool${cart.length !== 1 ? 's' : ''} (${formatPrice(cart.reduce((total, item) => total + item.price, 0))})`
                                                        }
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-8">
                                            <div className="text-right">
                                                <div className="text-sm text-gray-500">Total {billingCycle === 'yearly' ? 'yearly' : 'monthly'}</div>
                                                <div className="text-2xl font-bold">
                                                    {selectedPlan || selectedTier ? formatPrice(calculateTotal()) : formatPrice(cart.reduce((total, item) => total + item.price, 0))}
                                                </div>
                                            </div>
                                            {((purchaseMethod === 'plan' && planCart.length > 0) || (purchaseMethod === 'tools' && cart.length > 0) || (!selectedPlan && !purchaseMethod && cart.length > 0)) && (
                                                <button
                                                    type="submit"
                                                    disabled={isSubmitting}
                                                    className="w-full bg-blue-600 text-white ml-6 px-6 py-3 rounded-lg hover:bg-blue-700 font-medium transition-colors duration-300 disabled:bg-gray-400"
                                                    onClick={() => { handleCheckout() }}
                                                >
                                                    {isSubmitting ? (
                                                        <div className="flex items-center justify-center">
                                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Loading...
                                                        </div>
                                                    ) : 'Proceed to Checkout →'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Tabs>

                    <div
                      onClick={() => navigate('/student-program')}
                      className="mt-24 mb-10 relative overflow-hidden rounded-3xl cursor-pointer w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
                    >
                      <img src={studentProgramBanner3} alt="Student Program" className="w-full h-auto" />
                    </div>

                    {/* EDUCATIONAL SECTION */}
                    <div className="mt-24 mb-16">
                        <div className="educational-cards-container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
                            <Card className="educational-card-item">
                                <CardHeader className="text-center">
                                    <GraduationCap className="mx-auto w-8 h-8 text-gray-600 mb-2" />
                                    <CardTitle>Students</CardTitle>
                                </CardHeader>
                                <CardContent className="text-center">
                                    <p className="text-gray-600">We're committed to empowering the next generation of filmmakers and creatives. That's why we offer a sponsored plan for students.</p>
                                </CardContent>
                                <CardFooter className="justify-center">
                                    <button className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700" onClick={() => {}}>Learn More</button>
                                </CardFooter>
                            </Card>
                            <Card className="educational-card-item">
                                <CardHeader className="text-center">
                                    <Building className="mx-auto w-8 h-8 text-gray-600 mb-2" />
                                    <CardTitle>Universities</CardTitle>
                                </CardHeader>
                                <CardContent className="text-center">
                                    <p className="text-gray-600">Producer Buddy's media and process-based workflows provide a comprehensive framework for learning and equip students with the necessary skills to succeed in their future careers.</p>
                                </CardContent>
                                <CardFooter className="justify-center">
                                    <button className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700" onClick={() => {}}>Learn More</button>
                                </CardFooter>
                            </Card>
                        </div>
                    </div>

                    {/* TOOL SELECTION MODAL FOR PLANS */}
                    <div id='toolSelectContainer' className={styles.toolSelectContainer} translate="no" suppressHydrationWarning={true}>
                        <div id='toolSelect' className={styles.toolSelect}>
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h2 id={styles.selectorTitle} className='text-2xl font-semibold leading-none tracking-tight'>
                                        Select {limit} Tool{limit !== 1 ? 's' : ''}
                                    </h2>
                                    {selectedPlan && plans[billingCycle] && plans[billingCycle][selectedPlan] && (
                                        <>
                                            <p className="text-sm text-gray-600 mt-1">{formatPrice(plans[billingCycle][selectedPlan].price)}/month plan + individual tool costs</p>
                                            <Badge className={billingCycle === 'yearly' ? 'bg-green-100 text-green-800 mt-2' : 'bg-blue-100 text-blue-800 mt-2'}>
                                                {billingCycle === 'yearly' ? '📅 Billed Yearly (Save 20%)' : '📅 Billed Monthly'}
                                            </Badge>
                                        </>
                                    )}
                                </div>
                                <IoMdCloseCircleOutline id={styles.closeButton} onClick={() => closeToolSelect()} />
                            </div>
                            <div className="grid gap-4">
                                {tools.filter(cat => cat.available).map((category) => (
                                    <Card key={category.id}>
                                        <CardHeader>
                                            <div className="flex items-center gap-3">
                                                {category.icon}
                                                <CardTitle>{category.name}</CardTitle>
                                                <ChevronRight className="w-4 h-4 text-gray-400" />
                                            </div>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="grid grid-cols-3 gap-4">
                                                {category.subTools.map((tool) => (
                                                    <div key={tool.id} className="p-4 rounded-lg border border-gray-200 hover:border-blue-500 transition-colors">
                                                        <div className="flex justify-between items-start mb-4">
                                                            <div>
                                                                <h4 className="font-medium text-gray-900">{tool.name}</h4>
                                                                <p className="text-sm text-gray-500 mt-1">{tool.description}</p>
                                                                <Badge className="bg-purple-100 text-purple-800 mt-2">{formatPrice(tool.price)}/mo</Badge>
                                                            </div>
                                                        </div>
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-sm text-gray-600">{planCart.some(item => item.id === tool.id) ? 'Remove' : 'Select Tool'}</span>
                                                            <div className='flex items-center'>
                                                                {planQuantity[tool.id - 1] > 0 && <FiMinus className={styles.quantityButtonsMinus} onClick={() => lessPlanQuantity(tool.id)} />}
                                                                <button
                                                                    onClick={() => {
                                                                        sumArray(planQuantity) === limit
                                                                            ? (planCart.some(item => item.id === tool.id) && removeFromPlanCart(tool.id))
                                                                            : (planCart.some(item => item.id === tool.id) ? removeFromPlanCart(tool.id) : addToPlanCart(tool))
                                                                    }}
                                                                    className={`p-2 rounded-full transition-colors ${sumArray(planQuantity) < limit ? (planCart.some(item => item.id === tool.id) ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'bg-blue-100 text-blue-600 hover:bg-blue-200') : (planCart.some(item => item.id === tool.id) ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'bg-gray-200 text-gray-600 hover:bg-gray-600')}`}
                                                                >
                                                                    {sumArray(planQuantity) < limit && (planCart.some(item => item.id === tool.id) ? <p className="w-5 text-sm">{planQuantity[tool.id - 1]}</p> : <FaCheck className="w-5 h-5" />)}
                                                                    {sumArray(planQuantity) === limit && (planCart.some(item => item.id === tool.id) ? <p className="w-5 text-sm">{planQuantity[tool.id - 1]}</p> : <p className="w-5 h-5" />)}
                                                                </button>
                                                                {planQuantity[tool.id - 1] > 0 && sumArray(planQuantity) < limit && <FiPlus className={styles.quantityButtonsPlus} onClick={() => addPlanQuantity(tool.id)} />}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>

                            {selectedPlan && plans[billingCycle] && plans[billingCycle][selectedPlan] && (
                                <div className="mt-8 sticky bottom-0 bg-white border-t-2 border-gray-200 pt-4">
                                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-4 shadow-sm">
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="font-bold text-gray-900 text-lg">Pricing Breakdown</h3>
                                            <Badge className={billingCycle === 'yearly' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
                                                {billingCycle === 'yearly' ? '📅 Yearly (Save 20%)' : '📅 Monthly'}
                                            </Badge>
                                        </div>
                                        <div className="space-y-3">
                                            {billingCycle === 'monthly' ? (
                                                <>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-700 font-medium">Plan Cost:</span>
                                                        <span className="font-bold text-blue-600">{formatPrice(plans[billingCycle][selectedPlan].price)}/month</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-700 font-medium">Tools Selected ({sumArray(planQuantity)} of {limit}):</span>
                                                        <span className="font-bold text-purple-600">{formatPrice(planCart.reduce((total, item) => total + item.price, 0))}/month</span>
                                                    </div>
                                                    <div className="border-t-2 border-gray-300 pt-3 mt-3 flex justify-between items-center">
                                                        <span className="font-bold text-gray-900 text-lg">Monthly Total:</span>
                                                        <span className="font-bold text-2xl text-green-600">{formatPrice(plans[billingCycle][selectedPlan].price + planCart.reduce((total, item) => total + item.price, 0))}/month</span>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-700 font-medium">Plan Cost (Monthly):</span>
                                                        <span className="font-bold text-blue-600">{formatPrice(plans[billingCycle][selectedPlan].price)}/month</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-700 font-medium">Tools Selected ({sumArray(planQuantity)} of {limit}):</span>
                                                        <span className="font-bold text-purple-600">{formatPrice(planCart.reduce((total, item) => total + item.price, 0))}/month</span>
                                                    </div>
                                                    <div className="flex justify-between items-center text-sm">
                                                        <span className="text-gray-600">Monthly Subtotal:</span>
                                                        <span className="font-medium text-gray-700">{formatPrice(plans.monthly[selectedPlan].price + planCart.reduce((total, item) => total + item.price, 0))}/month</span>
                                                    </div>
                                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                                        <div className="flex justify-between items-center text-sm mb-1">
                                                            <span className="text-green-800">Yearly (12 months):</span>
                                                            <span className="font-medium text-green-800 line-through">{formatPrice((plans.monthly[selectedPlan].price + planCart.reduce((total, item) => total + item.price, 0)) * 12)}</span>
                                                        </div>
                                                        <div className="flex justify-between items-center text-sm">
                                                            <span className="text-green-800 font-bold">20% Discount:</span>
                                                            <span className="font-bold text-green-600">-{formatPrice((plans.monthly[selectedPlan].price + planCart.reduce((total, item) => total + item.price, 0)) * 12 * 0.2)}</span>
                                                        </div>
                                                    </div>
                                                    <div className="border-t-2 border-gray-300 pt-3 mt-3 flex justify-between items-center">
                                                        <span className="font-bold text-gray-900 text-lg">Yearly Total:</span>
                                                        <span className="font-bold text-2xl text-green-600">{formatPrice((plans.monthly[selectedPlan].price + planCart.reduce((total, item) => total + item.price, 0)) * 12 * 0.8)}/year</span>
                                                    </div>
                                                    <p className="text-xs text-gray-600 text-center">Equivalent to {formatPrice((plans.monthly[selectedPlan].price + planCart.reduce((total, item) => total + item.price, 0)) * 0.8)}/month</p>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {sumArray(planQuantity) === limit ? (
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 rounded-xl hover:from-blue-700 hover:to-purple-700 font-bold text-lg transition-all duration-300 disabled:bg-gray-400 shadow-lg transform hover:scale-105"
                                            onClick={() => { handleCheckout() }}
                                        >
                                            {isSubmitting ? (
                                                <div className="flex items-center justify-center">
                                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Loading...
                                                </div>
                                            ) : (
                                                <span className="flex items-center justify-center gap-2">Proceed to Checkout <span className="text-xl">→</span></span>
                                            )}
                                        </button>
                                    ) : (
                                        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 text-center">
                                            <p className="text-yellow-800 font-semibold">Please select {limit - sumArray(planQuantity)} more tool{limit - sumArray(planQuantity) !== 1 ? 's' : ''} to continue</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <EnterpriseSupport
                isOpen={showEnterpriseChat}
                onClose={() => setShowEnterpriseChat(false)}
                user={{ user_id: activeUser, email: "" }}
                selectedPlan={selectedPlan}
                selectedTools={cart}
                onPaymentRedirect={(url) => window.location.href = url}
            />
        </>
    );
};

export default PricingPage;